@if (operation$ | async; as operation) {

  <!-- Deleted banner -->
  @if (operation?.deletedAt) {
    <nz-alert
      nzType="error"
      nzShowIcon
      [nzMessage]="'ოპერაცია წაიშალა'"
      [nzDescription]="deletedDescTpl"
      style="margin-bottom: 12px;">
    </nz-alert>
    <ng-template #deletedDescTpl>
      <div>
        წაიშალა: {{ operation?.deletedAt | date: 'dd/MM/yyyy HH:mm' }}
        &bull; წაშალა: {{ operation?.deletedBy?.fullName }}
      </div>
      @if (operation?.deletedReason) {
        <div style="margin-top: 4px; font-weight: 500; color: #cf1322;">
          მიზეზი: {{ operation?.deletedReason }}
        </div>
      }
    </ng-template>
  }

  <!-- Summary card -->
  <div class="summary" [ngClass]="'summary--' + operation.operationSource?.toLowerCase()">
    <div class="summary__head">
      <div class="summary__tags">
        <nz-tag [nzColor]="getSourceColor(operation.operationSource)" class="summary__tag">
          {{ 'OPERATION_SOURCE.' + operation?.operationSource | translate }}
        </nz-tag>
        <nz-tag nzColor="default" class="summary__tag">
          {{ 'PAYMENTTYPE.' + operation?.paymentType | translate }}
        </nz-tag>
        @if (operation?.isGift) {
          <nz-tag nzColor="magenta" class="summary__tag">
            <i nz-icon nzType="gift" nzTheme="outline"></i>
          </nz-tag>
        }
        @if (operation?.operationSource === 'SERVICE_PACKAGE' && packagePurchaseId()) {
          <a
            *ngxPermissionsOnly="['Admin', 'Administrator', 'Manager']"
            class="summary__tag cursor-pointer text-primary"
            (click)="openPackage(packagePurchaseId())"
          >
            {{ 'servicePackages.purchases.title' | translate }}
            <i nz-icon nzType="link" nzTheme="outline"></i>
          </a>
        }
      </div>
      <div class="summary__amount">
        @if (operation?.salePrice > 0) {
          <span class="amount">{{ operation?.salePrice }}</span>
          <span class="currency"><i class="lari lari-c"></i></span>
          <del class="amount-old">{{ operation?.price }}</del>
        } @else {
          <span class="amount">{{ operation?.price }}</span>
          <span class="currency"><i class="lari lari-c"></i></span>
        }
      </div>
    </div>

    <div class="summary__date">{{ operation?.operationDate | date: 'dd/MM/yyyy' }}</div>

    <div class="summary__divider"></div>

    <div class="summary__info">
      @if (operation.user) {
        <div class="kv">
          <span class="kv__k">{{ 'statistic.detailSearch.detailView.user' | translate }}</span>
          <a class="kv__link" (click)="viewUser(operation.user)">
            {{ operation?.user?.fullName }}
            <i nz-icon nzType="link" nzTheme="outline"></i>
          </a>
        </div>
      }
      @if (operation.employee) {
        <div class="kv">
          <span class="kv__k">{{ 'statistic.detailSearch.detailView.employee' | translate }}</span>
          <a class="kv__link" (click)="viewEmployee(operation.employee)">
            {{ operation?.employee?.user?.fullName }}
            <i nz-icon nzType="link" nzTheme="outline"></i>
          </a>
        </div>
      }
      <div class="kv">
        <span class="kv__k">{{ 'statistic.detailSearch.detailView.created' | translate }}</span>
        <span class="kv__v">{{ operation?.createdAt | date: 'dd/MM/yyyy HH:mm' }} &bull; {{ operation?.createdBy?.fullName }}</span>
      </div>
      @if (!(operation.operationSource === 'BOOKING' && comments().length > 0) && operation.description) {
        <div class="kv">
          <span class="kv__k">{{ 'statistic.detailSearch.detailView.comment' | translate }}</span>
          <span class="kv__v kv__v--wrap">{{ operation.description }}</span>
        </div>
      }
    </div>
  </div>

  <!-- Comment thread (BOOKING only) -->
  @if (operation.operationSource === 'BOOKING' && comments().length > 0) {
    <div class="comments">
      <div class="comments__title">
        <i nz-icon nzType="message" nzTheme="outline"></i>
        {{ 'visitComments.tabTitle' | translate }}
      </div>
      <nz-timeline>
        @for (comment of comments(); track comment.id) {
          <nz-timeline-item [nzColor]="comment.isPinned ? 'blue' : 'gray'">
            <div class="comment">
              <div class="comment__head">
                @if (comment.isPinned) {
                  <i nz-icon nzType="pushpin" nzTheme="fill" style="color: #1890ff; font-size: 11px;"></i>
                }
                <span class="comment__author">{{ comment.createdBy?.fullName }}</span>
                <span class="comment__time">{{ comment.createdAt | date: 'dd/MM HH:mm' }}</span>
                @if (comment.isFromDescription) {
                  <nz-tag nzColor="default" class="comment__badge">{{ 'visitComments.fromDescription' | translate }}</nz-tag>
                }
              </div>
              <div class="comment__body">{{ comment.text }}</div>
            </div>
          </nz-timeline-item>
        }
      </nz-timeline>
    </div>
  }

  <!-- Operation details -->
  @if (operationDetails$ | async; as operationDetails) {
    <div class="details">

      <!-- BOOKING: Procedures -->
      @if (operation?.operationSource === 'BOOKING') {
        @if (getEvents(operationDetails); as events) {
          @if (events.length) {
            <nz-card [nzTitle]="'statistic.detailSearch.detailView.procedures' | translate" nzSize="small">
              @for (detail of events; track detail; let last = $last) {
                <ng-template #procTitle>
                  <span>{{ detail?.service?.name }}</span>
                  @if (detail?.packagePurchaseId) {
                    <nz-tag nzColor="cyan" style="margin-left: 8px; font-size: 12px;">პაკეტი</nz-tag>
                  }
                </ng-template>
                <nz-descriptions [nzTitle]="procTitle" nzBordered nzSize="small">
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.reservationDate' | translate }}">
                    {{ detail?.booking?.bookingDate }}
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.time' | translate }}">
                    {{ detail?.time }}
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.fixatedExpense' | translate }}">
                    {{ detail?.fixedSpend || 0 }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.additionalExpense' | translate }}">
                    {{ detail?.additionalExpence || 0 }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.nameLastName' | translate }}">
                    {{ detail?.user?.fullName }}
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.price' | translate }}">
                    {{ detail?.price }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.discountPrice' | translate }}">
                    {{ detail?.salePrice || 0 }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="დღგ-ით დასაბეგრი">
                    {{ detail?.isVatIncluded ? 'დიახ' : 'არა' }}
                  </nz-descriptions-item>
                  @if (detail?.isVatIncluded) {
                    <nz-descriptions-item nzTitle="დღგ თანხა">
                      {{ detail?.vatAmount | number: '1.0-2' }} <i class="lari lari-c"></i>
                    </nz-descriptions-item>
                  }
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employee' | translate }}">
                    {{ detail?.employee?.user?.fullName }}
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.position' | translate }}">
                    {{ detail?.employee?.position?.name }}
                  </nz-descriptions-item>
                  @if (detail?.fixedPercent) {
                    <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employee%' | translate }}">
                      {{ detail?.fixedPercent }} %
                    </nz-descriptions-item>
                  }
                  @if (detail?.fixedPrice) {
                    <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employeeSalary' | translate }}">
                      {{ detail?.fixedPrice }} <i class="lari lari-c"></i>
                    </nz-descriptions-item>
                  }
                  <nz-descriptions-item nzSpan="24" nzTitle="{{ 'statistic.detailSearch.detailView.comment' | translate }}">
                    {{ detail?.description }}
                  </nz-descriptions-item>
                </nz-descriptions>
                @if (!last) {
                  <nz-divider></nz-divider>
                }
              }
            </nz-card>
          }
        }

        <!-- Spec procedures -->
        @if (getSpecEvents(operationDetails); as specServices) {
          @if (specServices.length) {
            <nz-card nzTitle="სპეც პროცედურები" nzSize="small">
              @for (detail of specServices; track detail; let last = $last) {
                <nz-descriptions [nzTitle]="detail?.service?.name" nzBordered nzSize="small">
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employee' | translate }}">
                    {{ detail?.employee?.user?.fullName }}
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.position' | translate }}">
                    {{ detail?.employee?.position?.name }}
                  </nz-descriptions-item>
                  @if (detail?.fixedPercent) {
                    <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employee%' | translate }}">
                      {{ detail?.fixedPercent }} %
                    </nz-descriptions-item>
                  }
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.price' | translate }}">
                    {{ detail?.price }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.discountPrice' | translate }}">
                    {{ detail?.salePrice || 0 }} <i class="lari lari-c"></i>
                  </nz-descriptions-item>
                  @if (detail?.fixedPrice) {
                    <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.employeeSalary' | translate }}">
                      {{ detail?.fixedPrice }} <i class="lari lari-c"></i>
                    </nz-descriptions-item>
                  }
                </nz-descriptions>
                @if (!last) {
                  <nz-divider></nz-divider>
                }
              }
            </nz-card>
          }
        }

        <!-- BOOKING: Products -->
        @if (getProducts(operationDetails); as products) {
          @if (products.length) {
            <nz-card [nzTitle]="'statistic.detailSearch.detailView.products' | translate" nzSize="small">
              <nz-table nzTemplateMode nzSize="small">
                <thead>
                  <tr>
                    <th>{{ 'COLUMNS.barcode' | translate }}</th>
                    <th>{{ 'COLUMNS.productName' | translate }}</th>
                    <th>{{ 'COLUMNS.service' | translate }}</th>
                    <th>{{ 'COLUMNS.unitGram' | translate }}</th>
                    <th>{{ 'COLUMNS.unitQuantity' | translate }}</th>
                    <th>{{ 'COLUMNS.price' | translate }}</th>
                    <th>{{ 'COLUMNS.salePrice' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (detail of products; track detail) {
                    <tr>
                      <td>{{ detail?.product?.barcode }}</td>
                      <td>{{ detail?.product?.name }}</td>
                      <td>{{ detail?.service?.name }}</td>
                      <td>{{ detail?.unitGram }}</td>
                      <td>{{ detail?.unitQuantity }}</td>
                      <td>{{ detail?.price }} <i class="lari lari-c"></i></td>
                      <td>{{ detail?.salePrice }} <i class="lari lari-c"></i></td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </nz-card>
          }
        }

        <!-- BOOKING: Salary -->
        @if (getSalary(operationDetails); as salaries) {
          @if (salaries.length) {
            <nz-card [nzTitle]="'statistic.detailSearch.detailView.salary' | translate" nzSize="small">
              <nz-table nzTemplateMode nzSize="small">
                <thead>
                  <tr>
                    <th>{{ 'COLUMNS.employee' | translate }}</th>
                    <th>{{ 'COLUMNS.fixedPercent' | translate }}</th>
                    <th>{{ 'COLUMNS.employeeSalary' | translate }}</th>
                    <th>{{ 'COLUMNS.productName' | translate }}</th>
                    <th>{{ 'COLUMNS.fixedSpend' | translate }}</th>
                    <th>{{ 'COLUMNS.additionalExpense' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (detail of salaries; track detail) {
                    <tr>
                      <td>{{ detail?.employee?.user?.fullName }}</td>
                      <td>{{ detail?.fixedPercent }}</td>
                      <td>{{ detail?.employeeSalary }} <i class="lari lari-c"></i></td>
                      <td>{{ detail?.product?.name }}</td>
                      <td>{{ detail?.fixedSpend }}</td>
                      <td>{{ detail?.additionalExpence || 0 }}</td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </nz-card>
          }
        }
      }

      <!-- CASHIER -->
      @if (operation?.operationSource === 'CASHIER') {
        <nz-card [nzTitle]="'statistic.detailSearch.detailView.products' | translate" nzSize="small">
          @for (detail of getCashierAdd(operationDetails); track detail) {
            <nz-descriptions [nzTitle]="'statistic.detailSearch.detailView.addAmount' | translate" nzBordered nzSize="small">
              <nz-descriptions-item [nzTitle]="'statistic.detailSearch.detailView.amount' | translate">
                {{ detail?.price }} <i class="lari lari-c"></i>
              </nz-descriptions-item>
            </nz-descriptions>
          }
        </nz-card>
      }

      <!-- SALARY -->
      @if (operation?.operationSource === 'SALARY') {
        <nz-card [nzTitle]="'statistic.detailSearch.detailView.salaryDisbursement' | translate" nzSize="small">
          <nz-table nzTemplateMode nzSize="small">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.employee' | translate }}</th>
                <th>{{ 'COLUMNS.employeeSalary' | translate }}</th>
                <th>{{ 'COLUMNS.comment' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (detail of getSalaryOut(operationDetails); track detail) {
                <tr>
                  <td>{{ detail?.employee?.user?.fullName }}</td>
                  <td>{{ detail?.employeeSalary }} <i class="lari lari-c"></i></td>
                  <td>{{ detail?.description }}</td>
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-card>
      }

      <!-- Balance -->
      @if (getBalance(operationDetails); as balances) {
        @if (balances.length) {
          <nz-card [nzTitle]="'statistic.detailSearch.detailView.balance' | translate" nzSize="small">
            <nz-table nzTemplateMode nzSize="small">
              <thead>
                <tr>
                  <th>{{ 'COLUMNS.operationType' | translate }}</th>
                  <th>{{ 'COLUMNS.user' | translate }}</th>
                  <th>{{ 'COLUMNS.minutes' | translate }}</th>
                  <th>{{ 'COLUMNS.balance' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (detail of balances; track detail) {
                  <tr>
                    <td>{{ 'OPERATION_TYPE.' + detail?.operationType | translate }}</td>
                    <td>{{ detail?.user?.fullName }}</td>
                    <td>{{ detail?.minutes }}</td>
                    <td>{{ detail?.balance }}</td>
                  </tr>
                }
              </tbody>
            </nz-table>
          </nz-card>
        }
      }

      <!-- EXPENCE -->
      @if (operation?.operationSource === 'EXPENCE') {
        <nz-card [nzTitle]="'statistic.detailSearch.detailView.expenses' | translate" nzSize="small">
          <nz-table nzTemplateMode nzSize="small">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.expenceCategory' | translate }}</th>
                <th>{{ 'statistic.detailSearch.detailView.amount' | translate }}</th>
                <th>{{ 'COLUMNS.comment' | translate }}</th>
                <th>{{ 'COLUMNS.bankTransfer' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (detail of getExpence(operationDetails); track detail) {
                <tr>
                  <td>{{ detail?.expenceCategory?.name }}</td>
                  <td>{{ detail?.price }} <i class="lari lari-c"></i></td>
                  <td>{{ detail?.description }}</td>
                  <td>{{ detail?.bankTransferPrice ? 'კი' : 'არა' }}</td>
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-card>
      }

      <!-- REALIZATION -->
      @if (operation?.operationSource === 'REALIZATION') {
        <nz-card [nzTitle]="'statistic.detailSearch.detailView.Products' | translate" nzSize="small">
          <nz-table nzTemplateMode nzSize="small">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.barcode' | translate }}</th>
                <th>{{ 'COLUMNS.productName' | translate }}</th>
                <th>{{ 'COLUMNS.unitGram' | translate }}</th>
                <th>{{ 'COLUMNS.unitQuantity' | translate }}</th>
                <th>{{ 'COLUMNS.price' | translate }}</th>
                <th>{{ 'COLUMNS.salePrice' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (detail of getProducts(operationDetails); track detail) {
                <tr>
                  <td>{{ detail?.product?.barcode }}</td>
                  <td>{{ detail?.product?.name }}</td>
                  <td>{{ detail?.unitGram }}</td>
                  <td>{{ detail?.unitQuantity }}</td>
                  <td>{{ detail?.price }} <i class="lari lari-c"></i></td>
                  <td>{{ detail?.salePrice }} <i class="lari lari-c"></i></td>
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-card>

        <nz-card [nzTitle]="'statistic.detailSearch.detailView.salary' | translate" nzSize="small">
          <nz-table nzTemplateMode nzSize="small">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.employee' | translate }}</th>
                <th>{{ 'COLUMNS.fixedPercent' | translate }}</th>
                <th>{{ 'COLUMNS.employeeSalary' | translate }}</th>
                <th>{{ 'COLUMNS.productName' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (detail of getSalary(operationDetails); track detail) {
                <tr>
                  <td>{{ detail?.employee?.user?.fullName }}</td>
                  <td>{{ detail?.fixedPercent }}</td>
                  <td>{{ detail?.employeeSalary }} <i class="lari lari-c"></i></td>
                  <td>{{ detail?.product?.name }}</td>
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-card>
      }

      <!-- PURCHASE -->
      @if (operation?.operationSource === 'PURCHASE') {
        <nz-card [nzTitle]="'statistic.detailSearch.detailView.Products' | translate" nzSize="small">
          <nz-table nzTemplateMode nzSize="small">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.barcode' | translate }}</th>
                <th>{{ 'COLUMNS.productName' | translate }}</th>
                <th>{{ 'COLUMNS.unitQuantity' | translate }}</th>
                <th>{{ 'COLUMNS.price' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (detail of getProducts(operationDetails); track detail) {
                <tr>
                  <td>{{ detail?.product?.barcode }}</td>
                  <td>{{ detail?.product?.name }}</td>
                  <td>{{ detail?.unitQuantity }}</td>
                  <td>{{ detail?.price }} <i class="lari lari-c"></i></td>
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-card>
      }

      <!-- SERVICE_PACKAGE -->
      @if (operation?.operationSource === 'SERVICE_PACKAGE') {
        <nz-card nzTitle="პაკეტის დეტალები" nzSize="small">
          @if (packageItems().length) {
            @for (it of packageItems(); track it.id; let last = $last) {
              <nz-descriptions [nzTitle]="it.service?.name || it.serviceId" nzBordered nzSize="small">
                <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.price' | translate }}">
                  {{ it.price }} <i class="lari lari-c"></i>
                </nz-descriptions-item>
                <nz-descriptions-item nzTitle="{{ 'statistic.detailSearch.detailView.discountPrice' | translate }}">
                  {{ it.salePrice }} <i class="lari lari-c"></i>
                </nz-descriptions-item>
                <nz-descriptions-item nzTitle="{{ 'servicePackages.editModal.quantity' | translate }}">
                  {{ it.quantity }}
                </nz-descriptions-item>
              </nz-descriptions>
              @if (!last) { <nz-divider></nz-divider> }
            }
          } @else {
            <div class="text-gray-500">
              {{ operation?.description }} — {{ operation?.price }} <i class="lari lari-c"></i>
            </div>
          }
        </nz-card>

        <!-- SERVICE_PACKAGE: Salary -->
        @if (getSalary(operationDetails); as salaries) {
          @if (salaries.length) {
            <nz-card [nzTitle]="'statistic.detailSearch.detailView.salary' | translate" nzSize="small">
              <nz-table nzTemplateMode nzSize="small">
                <thead>
                  <tr>
                    <th>{{ 'COLUMNS.employee' | translate }}</th>
                    <th>{{ 'COLUMNS.fixedPercent' | translate }}</th>
                    <th>{{ 'COLUMNS.employeeSalary' | translate }}</th>
                    <th>{{ 'COLUMNS.productName' | translate }}</th>
                    <th>{{ 'COLUMNS.fixedSpend' | translate }}</th>
                    <th>{{ 'COLUMNS.additionalExpense' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (detail of salaries; track detail) {
                    <tr>
                      <td>{{ detail?.employee?.user?.fullName }}</td>
                      <td>{{ detail?.fixedPercent }}</td>
                      <td>{{ detail?.employeeSalary }} <i class="lari lari-c"></i></td>
                      <td>{{ detail?.product?.name }}</td>
                      <td>{{ detail?.fixedSpend }}</td>
                      <td>{{ detail?.additionalExpence || 0 }}</td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </nz-card>
          }
        }
      }

    </div>
  }

  <!-- Consignations -->
  @if (operation.consignations && operation.consignations.length) {
    <nz-card [nzTitle]="'statistic.consignation.column.consignment' | translate" nzSize="small" style="margin-top: 8px;">
      <nz-table #consignationTable [nzData]="operation.consignations" nzSize="small" [nzFrontPagination]="false" [nzShowPagination]="false">
        <thead>
          <tr>
            <th>{{ 'statistic.consignation.paymentMethod' | translate }}</th>
            <th>{{ 'COLUMNS.price' | translate }}</th>
            <th>{{ 'COLUMNS.cash' | translate }}</th>
            <th>{{ 'COLUMNS.card' | translate }}</th>
            <th>{{ 'statistic.consignation.column.consignment' | translate }}</th>
            <th>{{ 'statistic.consignation.column.paid' | translate }}</th>
            <th>{{ 'COLUMNS.createdAt' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (data of consignationTable.data; track data) {
            <tr>
              <td>{{ data?.paymentType | paymentType | translate }}</td>
              <td>{{ data?.price }}</td>
              <td>{{ data?.cash }}</td>
              <td>{{ data?.card }}</td>
              <td>{{ data?.consignation }}</td>
              <td>{{ data?.consignationPayed }}</td>
              <td>{{ data?.transactionDate | date: 'dd/MM/yyyy HH:mm' }}</td>
            </tr>
          }
        </tbody>
      </nz-table>
      <nz-divider></nz-divider>
      @for (consignations of operation.consignations; track consignations) {
        @if (consignations?.consignationToEmployees) {
          <nz-table #consignationEmplTable [nzData]="consignations?.consignationToEmployees" nzSize="small" [nzFrontPagination]="false" [nzShowPagination]="false">
            <thead>
              <tr>
                <th>{{ 'COLUMNS.employee' | translate }}</th>
                <th>{{ 'COLUMNS.fixedPercent' | translate }}</th>
                <th>{{ 'statistic.consignation.column.price' | translate }}</th>
                <th>{{ 'statistic.consignation.column.issued' | translate }}</th>
                <th>{{ 'statistic.consignation.column.paid' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (data of consignationEmplTable.data; track data) {
                <tr>
                  <td>{{ data?.employee?.user?.fullName }}</td>
                  <td>{{ data?.employeePercent }}</td>
                  <td>{{ data?.servicePrice | number: '.0-2' }}</td>
                  <td>{{ data?.employeeTotalToPay | number: '.0-2' }}</td>
                  <td>{{ data?.employeePaid | number: '.0-2' }}</td>
                </tr>
              }
            </tbody>
          </nz-table>
        }
      }
    </nz-card>
  }

  <!-- Transactions -->
  @if (operation.transaction) {
    <nz-card [nzTitle]="'statistic.transaction.column.transaction' | translate" nzSize="small" style="margin-top: 8px;">
      <nz-table nzTemplateMode nzSize="small">
        <thead>
          <tr>
            <th>{{ 'statistic.transaction.column.transaction#' | translate }}</th>
            <th>{{ 'statistic.transaction.column.transactionType' | translate }}</th>
            <th>{{ 'statistic.transaction.column.paymentMethod' | translate }}</th>
            <th>{{ 'statistic.transaction.column.price' | translate }}</th>
            <th>{{ 'statistic.transaction.column.cash' | translate }}</th>
            <th>{{ 'statistic.transaction.column.card' | translate }}</th>
            <th>{{ 'statistic.transaction.column.bank' | translate }}</th>
            <th>{{ 'statistic.transaction.column.bankName' | translate }}</th>
            <th>{{ 'statistic.transaction.column.balance(GEL)' | translate }}</th>
            <th>{{ 'statistic.transaction.column.created' | translate }}</th>
            <th>{{ 'statistic.transaction.column.comment' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (transaction of operation['transaction']; track transaction) {
            <tr [class.transaction-deleted]="transaction?.deletedAt">
              <td>{{ transaction?.id }}</td>
              <td>{{ transaction?.transactionType | transactionType | translate }}</td>
              <td>{{ transaction?.paymentType | paymentType | translate }}</td>
              <td>{{ transaction?.price }}</td>
              <td>{{ transaction?.cash }}</td>
              <td>{{ transaction?.card }}</td>
              <td>{{ transaction?.bank }}</td>
              <td>{{ transaction?.bankId }}</td>
              <td>{{ transaction?.balance }}</td>
              <td>{{ transaction?.transactionDate | date: 'dd/MM/yyyy HH:mm' }}</td>
              <td>
                @if (operation.description) {
                  <span
                    nz-popover
                    [nzPopoverContent]="operation.description"
                    nzPopoverPlacement="topLeft"
                    style="cursor: pointer;"
                  >
                    <i nz-icon nzType="message" nzTheme="outline" style="color: #1890ff;"></i>
                  </span>
                }
              </td>
              <td>
                <ng-container *ngxPermissionsOnly="['Manager', 'Admin']">
                  @if (transaction.transactionType === 'CONSIGNATION' && !transaction.deletedAt && !operation.deletedAt) {
                    <button
                      nz-button nzType="text" nzSize="small" nzDanger
                      nz-popconfirm
                      nzPopconfirmTitle="{{ 'employee.delete' | translate }}"
                      nzPopconfirmPlacement="bottom"
                      (nzOnConfirm)="delete(transaction.id)">
                      <i nz-icon nzType="delete"></i>
                    </button>
                  }
                </ng-container>
              </td>
            </tr>
          }
        </tbody>
      </nz-table>
    </nz-card>
  }
}
